import cn from 'classnames';
import { useConditionalEndpointOIDCUsersQuery } from '#api/endpoint-oidc-users.queries.ts';
import { useConditionalRoleQuery } from '#api/roles.queries.ts';
import { FilterPill } from '#components/atoms/pill/filter-pill/FilterPill.tsx';
import {
  ANY_DEVICE_OWNER_FLAG,
  NO_DEVICE_OWNER_FLAG,
} from '#components/patterns/entity-select/device-owner/option-constants.ts';
import { NO_ROLE_FLAG } from '#components/patterns/entity-select/role/option-constants.ts';
import { HOST_FILTERS, useHostFilters } from '#hooks/useHostFilters.ts';
import { HOST_KIND_LABELS, isHostKind } from '#util/host.ts';
import { HostFilterPill } from './HostFilterPill.tsx';
import styles from './FilterPills.module.css';

type Props = {
  className?: string;
};

/**
 * Renders the active host filter pills (role, device owner, tags, etc.) as a
 * dismissible list. Each pill resolves its own display label — fetching the
 * role name or device-owner email when the raw filter value is an ID — and
 * removes its filter from the URL params when clicked.
 */
export function FilterPills({ className }: Props) {
  const { setParams, filters, tags } = useHostFilters();

  const roleId = filters.roleID;
  const ownerId = filters.endpointOIDCUserID;

  const needsRoleName = typeof roleId === 'string' && roleId !== NO_ROLE_FLAG;
  const roleQuery = useConditionalRoleQuery(typeof roleId === 'string' ? roleId : '', { enabled: needsRoleName });
  const ownerQuery = useConditionalEndpointOIDCUsersQuery(
    typeof ownerId === 'string' && ownerId !== NO_DEVICE_OWNER_FLAG && ownerId !== ANY_DEVICE_OWNER_FLAG ?
      ownerId
    : null
  );

  function resolveLabel(filterName: (typeof HOST_FILTERS)[number]): string | undefined {
    if (filterName === 'type' && typeof filters.type === 'string' && isHostKind(filters.type)) {
      return `Type: ${HOST_KIND_LABELS[filters.type]}`;
    }
    if (filterName === 'roleID' && typeof roleId === 'string') {
      if (roleId === NO_ROLE_FLAG) return 'Role: No role';
      const roleName = roleQuery.data?.data.name;
      if (roleName) return `Role: ${roleName}`;
    }
    if (filterName === 'endpointOIDCUserID' && typeof ownerId === 'string') {
      if (ownerId === NO_DEVICE_OWNER_FLAG) return 'Device owner: None';
      if (ownerId === ANY_DEVICE_OWNER_FLAG) return 'Device owner: Any';
      const email = ownerQuery.data?.email;
      if (email) return `Device owner: ${email}`;
    }
    return undefined;
  }

  // Network filter is handled by dropdown, tags are rendered individually below
  const EXCLUDED_FILTERS = ['networkID', 'tags'] satisfies Array<(typeof HOST_FILTERS)[number]>;

  const clearFilter = (filterNameToClear: string) => {
    const newParams = Object.fromEntries(
      Object.entries(filters).map(([filterName, value]) => {
        if (filterName !== filterNameToClear) {
          return [`filter.${filterName}`, value];
        }
        return [`filter.${filterName}`, undefined];
      })
    ) satisfies Partial<Record<`filter.${(typeof HOST_FILTERS)[number]}`, boolean | string | string[] | undefined>>;

    // Clear cursor when changing filters, otherwise it will be invalid
    setParams({ ...newParams, cursor: undefined });
  };

  const removeTag = (tagToRemove: string) => {
    const remaining = tags.filter((t) => t !== tagToRemove);
    setParams({
      'filter.tags': remaining.length > 0 ? remaining : undefined,
      cursor: undefined,
    });
  };

  const filterNames = Object.keys(filters);

  const pills = HOST_FILTERS.filter((filterName) => filterNames.includes(filterName))
    .filter((filterName) => !EXCLUDED_FILTERS.includes(filterName))
    // An unrecognized `filter.type` value is silently dropped by the query layer (see
    // createEncodedGetHostsFilter), so a pill for it would claim a filter the list isn't applying.
    .filter((filterName) => filterName !== 'type' || (typeof filters.type === 'string' && isHostKind(filters.type)))
    .map((filterName) => (
      <li key={filterName}>
        <HostFilterPill
          filterName={filterName}
          label={resolveLabel(filterName)}
          onClick={() => clearFilter(filterName)}
        />
      </li>
    ));

  const tagPills = tags.map((tag) => (
    <li key={`tag:${tag}`}>
      <FilterPill label={tag} onClick={() => removeTag(tag)} />
    </li>
  ));

  const allPills = [...pills, ...tagPills];

  // If there are no pills, don't render an empty ul, it can throw off the layout
  if (!allPills.length) {
    return null;
  }

  return <ul className={cn(className, styles.FilterPills_list)}>{allPills}</ul>;
}
